import { z } from "zod";

// =========================================================================
// PROVIDERS
//
// Para añadir un provider nuevo:
//   1. Añadirlo a PixelProviderSchema
//   2. Definir su EventSchema, OptionsSchema (+ ResponseSchema si tiene
//      secrets en options), InputShape y ResponseShape
//   3. Registrarlo en AnyPixelInputShape, PixelConfigResponseSchema
//      y PixelClientConfigSchema
// =========================================================================

export const PixelProviderSchema = z.enum(["META", "GOOGLE", "TIKTOK"]);

// =========================================================================
// MODO
// =========================================================================

export const PixelModeSchema = z.enum(["CLIENT_ONLY", "SERVER_ONLY", "BOTH"]);

// =========================================================================
// EVENTOS por provider
// =========================================================================

export const MetaEventSchema = z.enum([
  "InitiateCheckout",
  "Purchase",
  "AddToCart",
  "AddPaymentInfo",
]);

export const GoogleEventSchema = z.enum([
  "begin_checkout",
  "purchase",
  "add_to_cart",
]);

export const TikTokEventSchema = z.enum([
  "InitiateCheckout",
  "Purchase",
  "AddToCart",
]);

// =========================================================================
// OPTIONS por provider (persisten en la columna JSONB `options` de la DB)
//
// Regla: si un campo de options es un secret server-side (api_secret,
// tokens embebidos), define también un *ResponseSchema que lo reemplaza
// por un *_hint enmascarado. Si options no tiene secrets, el mismo schema
// sirve para input y response.
// =========================================================================

export const MetaContentIdFormatSchema = z.enum([
  "shopify_{REGION}_{ProductId}",
  "shopify_{REGION}_{VariantId}",
  "{ProductId}",
  "{VariantId}",
]);

export const MetaOptionsSchema = z.object({
  content_id_format: MetaContentIdFormatSchema.default(
    "shopify_{REGION}_{ProductId}"
  ),
  send_add_to_cart: z.boolean().default(false),
  send_add_payment_info: z.boolean().default(false),
  test_event_code: z.string().optional(),
});

export const GoogleOptionsSchema = z.object({
  api_secret: z.string().optional(),
});

export const GoogleOptionsResponseSchema = GoogleOptionsSchema.omit({
  api_secret: true,
}).extend({ api_secret_hint: z.string().optional() });

export const TikTokOptionsSchema = z.object({
  send_add_to_cart: z.boolean().default(false),
  test_event_code: z.string().optional(),
});

// =========================================================================
// BASE SHAPE
// =========================================================================

const PixelConfigBaseShape = z.object({
  label: z.string().max(100).optional(),
  pixel_id: z.string().min(1),
  mode: PixelModeSchema.default("CLIENT_ONLY"),
  /**
   * En create el servicio siempre fija is_active = true.
   * En update, enviar false actúa como soft delete — no hay endpoint DELETE.
   */
  is_active: z.boolean().default(true),
});

// =========================================================================
// INPUT SHAPES (requests: create / update)
// Contienen credenciales en texto claro.
// =========================================================================

const MetaPixelInputShape = PixelConfigBaseShape.extend({
  provider: z.literal("META"),
  events_enabled: z.array(MetaEventSchema).min(1),
  options: MetaOptionsSchema,
  access_token: z.string().optional(),
});

const GooglePixelInputShape = PixelConfigBaseShape.extend({
  provider: z.literal("GOOGLE"),
  events_enabled: z.array(GoogleEventSchema).min(1),
  options: GoogleOptionsSchema,
});

const TikTokPixelInputShape = PixelConfigBaseShape.extend({
  provider: z.literal("TIKTOK"),
  events_enabled: z.array(TikTokEventSchema).min(1),
  options: TikTokOptionsSchema,
  access_token: z.string().optional(),
});

const AnyPixelInputShape = z.discriminatedUnion("provider", [
  MetaPixelInputShape,
  GooglePixelInputShape,
  TikTokPixelInputShape,
]);

// =========================================================================
// RESPONSE SHAPES
//
//
// El servicio es responsable de construir el *_hint antes de serializar
// (e.g. "••••••••" + últimos 4 chars). El schema garantiza que el tipo
// nunca incluya el secret en texto claro en una respuesta.
// =========================================================================

const MetaPixelResponseShape = MetaPixelInputShape.omit({
  access_token: true,
}).extend({ access_token_hint: z.string().optional() });

const GooglePixelResponseShape = GooglePixelInputShape.omit({
  options: true,
}).extend({ options: GoogleOptionsResponseSchema });

const TikTokPixelResponseShape = TikTokPixelInputShape.omit({
  access_token: true,
}).extend({ access_token_hint: z.string().optional() });

// =========================================================================
// HELPER — añade metadatos del servidor a un response shape
// =========================================================================

const withServerMeta = <T extends z.ZodRawShape>(shape: z.ZodObject<T>) =>
  shape.extend({
    id: z.string(),
    shop_id: z.string(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  });

// =========================================================================
// SCHEMA DE RESPUESTA UNIFICADO
// =========================================================================

export const PixelConfigResponseSchema = z.discriminatedUnion("provider", [
  withServerMeta(MetaPixelResponseShape),
  withServerMeta(GooglePixelResponseShape),
  withServerMeta(TikTokPixelResponseShape),
]);

// =========================================================================
// VALIDACIÓN CROSS-FIELD: credenciales requeridas según mode
// Separada de los shapes para poder reutilizarla en create y update.
// =========================================================================

const validateServerCredentials = (
  val: z.infer<typeof AnyPixelInputShape>,
  ctx: z.RefinementCtx
): void => {
  const requiresServer = val.mode === "SERVER_ONLY" || val.mode === "BOTH";
  if (!requiresServer) return;

  if (
    (val.provider === "META" || val.provider === "TIKTOK") &&
    !val.access_token
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["access_token"],
      message: `access_token es requerido para ${val.provider} en modo ${val.mode}`,
    });
  }

  if (val.provider === "GOOGLE" && !val.options.api_secret) {
    ctx.addIssue({
      code: "custom",
      path: ["options", "api_secret"],
      message: `options.api_secret es requerido para GOOGLE en modo ${val.mode}`,
    });
  }
};

// =========================================================================
// POST /admin/pixels
// =========================================================================

export const CreatePixelConfigRequestSchema = AnyPixelInputShape.superRefine(
  validateServerCredentials
);

// =========================================================================
// GET /admin/pixels
// =========================================================================

export const ListPixelConfigsResponseSchema = z.object({
  items: z.array(PixelConfigResponseSchema),
});

// =========================================================================
// PUT /admin/pixels/:id
// El provider no puede cambiar tras la creación, el frontend envía el
// objeto completo con el mismo provider.
// Soft delete: enviar is_active: false en lugar de llamar a DELETE.
//
// A diferencia de create, update NO aplica validateServerCredentials. Las
// respuestas solo exponen *_hint, así que una credencial omitida significa
// "conservar la guardada" — exigir reingreso obligaría al merchant a re-teclear
// secretos en cada edición. La invariante "modo servidor ⇒ existe credencial"
// es stateful (depende del valor ya almacenado), por lo que se valida en
// PixelService.update() contra la credencial resuelta (reingresada o conservada).
// =========================================================================

export const UpdatePixelConfigRequestSchema = AnyPixelInputShape;

// =========================================================================
// POST /admin/pixels/:id/test
// Dispara un evento de prueba server-side para un píxel guardado (botón
// "Probar"). `ok`/`message` describen el RESULTADO de la prueba (p. ej. Meta
// rechazó el token) y viajan con HTTP 200 — un 4xx se reserva para errores de
// la propia petición (píxel inexistente, modo solo-cliente, falta de credencial
// o de test_event_code).
// =========================================================================

export const PixelTestResponseSchema = z.object({
  provider: PixelProviderSchema,
  side: z.literal("server"),
  ok: z.boolean(),
  status: z.number().nullable(),
  message: z.string(),
});

// =========================================================================
// CLIENT SCHEMAS
// =========================================================================

const MetaClientPixelSchema = MetaPixelInputShape.pick({
  provider: true,
  pixel_id: true,
  events_enabled: true,
  options: true,
});

const GoogleClientPixelSchema = GooglePixelInputShape.pick({
  provider: true,
  pixel_id: true,
  events_enabled: true,
});

const TikTokClientPixelSchema = TikTokPixelInputShape.pick({
  provider: true,
  pixel_id: true,
  events_enabled: true,
  options: true,
});

export const PixelClientConfigSchema = z.discriminatedUnion("provider", [
  MetaClientPixelSchema,
  GoogleClientPixelSchema,
  TikTokClientPixelSchema,
]);

// =========================================================================
// BOOTSTRAP - configuración de pixels para el widget
//
// Reemplaza TrackingSchema en bootstrap.schema.ts.
// event_id: ID de deduplicación para correlacionar eventos client + server.
// =========================================================================

export const BootstrapTrackingSchema = z.object({
  pixels: z.array(PixelClientConfigSchema),
  event_id: z.string(),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type PixelProvider = z.infer<typeof PixelProviderSchema>;
export type PixelMode = z.infer<typeof PixelModeSchema>;

export type MetaEvent = z.infer<typeof MetaEventSchema>;
export type GoogleEvent = z.infer<typeof GoogleEventSchema>;
export type TikTokEvent = z.infer<typeof TikTokEventSchema>;

export type MetaOptions = z.infer<typeof MetaOptionsSchema>;
export type GoogleOptions = z.infer<typeof GoogleOptionsSchema>;
export type TikTokOptions = z.infer<typeof TikTokOptionsSchema>;

export type PixelConfigResponse = z.infer<typeof PixelConfigResponseSchema>;
export type CreatePixelConfigRequest = z.infer<
  typeof CreatePixelConfigRequestSchema
>;
export type UpdatePixelConfigRequest = z.infer<
  typeof UpdatePixelConfigRequestSchema
>;
export type ListPixelConfigsResponse = z.infer<
  typeof ListPixelConfigsResponseSchema
>;
export type PixelTestResponse = z.infer<typeof PixelTestResponseSchema>;
export type PixelClientConfig = z.infer<typeof PixelClientConfigSchema>;
export type BootstrapTracking = z.infer<typeof BootstrapTrackingSchema>;
